import React, { useState, useContext, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useResolvedPath } from "react-router";
import { Typography, Tooltip, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import MUIButton from "./../UI/MUIButton/MUIButton";
import MUISelect from "./../UI/MUISelect/MUISelect";
import Rankings from "./Rankings";
import SentimentTable from "./SentimentTable";
import { useReactToPrint } from "react-to-print";
import DownloadIcon from "@mui/icons-material/Download";
import classes from "./BrandIndexBreakups.module.css";
import { DiscoveryContext } from "../../luminaire/BrandAudit/discovery-context";
import { sanitizeObject, transformDriverData } from "../../utils/helpers";
import api from "../../BrandOnBoarding/api";
import SentimentDriversTable from "./SentimentDrivers";
import Spinner from "../Spinner/Spinner";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import useBaseResponsesModal from "../../Component/BaseResponseComponent/useBaseResponsesModal";
import BaseResponsesModal from "../../Component/BaseResponseComponent/BaseResponsesModal";
import { formatSectionLabel } from "../../utils/helpers";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import BrandPillarsTable from "./BrandPillarsTable";
import ProductDimensionsTable from "./ProductDimensionsTable";
import PurchaseCriteriaTable from "./PurchaseCriteriaTable";
import RBIFormulaBreakdown from "./RBIFormulaBreakdown";
import {
  getBrandPillarsAvg,
  getProductDimensionsAvg,
  getPurchaseCriteriaAvg,
} from "../../utils/brandIndexCalc";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function BrandAuditIndexBreakups({
  type,
  data,
  isMonitoring,
  brandProduct,
  isOpen,
  remountChild,
  llm,
  category,
  market,
  persona,
  selectedInput,
  fromBrandAudit = false,
  compIndexScoreMetaData = [],
  ctx = null,
  visibleSections = null,
  isCategoryAnalysis = false,
}) {
  const rankLabel = isCategoryAnalysis ? "Rank" : ENTITY_LABELS.rank;
  const { pathname } = useResolvedPath();
  let selectedOptionShow = type == "brand"
    ? ENTITY_LABELS.brand
    : ENTITY_LABELS.product;
  const _defaultCtx = useContext(DiscoveryContext);
  const discoveryCtx = ctx || _defaultCtx;
  const [savedMatchIndices, setSaveMatchIndices] = useState([]);
  const [saveReachScore, setSavedReachScore] = useState();
  const [userMatchedIndices, setUserMatchedIndices] = useState([]);
  const [userReachScore, setUserReachScore] = useState();
  const [loadingUpdatedRank, setLoadingUpdatedRank] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sentimentDrivers, setSentimentDrivers] = useState(
    data?.sentiementKeyDrivers
  );
  const [isDriversLodaing, setIsDriversLoading] = useState(false);
  const [selectedDriverFactor, setSelectedDriverFactor] = useState("");
  const isFactorGroupedDrivers =
    (sentimentDrivers?.positive && !Array.isArray(sentimentDrivers.positive) && typeof sentimentDrivers.positive === "object") ||
    (sentimentDrivers?.negative && !Array.isArray(sentimentDrivers.negative) && typeof sentimentDrivers.negative === "object");

  const driverFactorOptions = isFactorGroupedDrivers
    ? Array.from(new Set([
      ...Object.keys(sentimentDrivers?.positive || {}),
      ...Object.keys(sentimentDrivers?.negative || {}),
    ]))
    : [];

  useEffect(() => {
    if (isFactorGroupedDrivers && driverFactorOptions.length && !driverFactorOptions.includes(selectedDriverFactor)) {
      setSelectedDriverFactor(driverFactorOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFactorGroupedDrivers, driverFactorOptions.join("|")]);

  const positiveDriversToShow = isFactorGroupedDrivers
    ? (sentimentDrivers?.positive?.[selectedDriverFactor] ?? [])
    : (sentimentDrivers?.positive ?? []);
  const negativeDriversToShow = isFactorGroupedDrivers
    ? (sentimentDrivers?.negative?.[selectedDriverFactor] ?? [])
    : (sentimentDrivers?.negative ?? []);

  const [currentDatasetCount, setCurrentDatasetCount] = useState(5);
  const brandAuditContext = discoveryCtx.brand_scores[llm]?.[category]?.[market]?.[persona]
  const webSocketBaseURL = process.env.REACT_APP_ORCHESTRATOR_BASE_URL;
  const { user } = useContext(AuthContext);
  const isCompanyUser = user?.user_role?.role?.name === "Company user";
  const showRank     = !visibleSections || visibleSections.includes("rank");
  const showSentiment = !visibleSections || visibleSections.includes("sentiment");
  const showDrivers  = !visibleSections || visibleSections.includes("sentimentDrivers");
  const showPillars  = !visibleSections || visibleSections.includes("brandPillars");
  const showPurchase = !visibleSections || visibleSections.includes("purchaseCriteria");
  const showFormula  = !visibleSections || visibleSections.includes("rbiFormula");
  useEffect(() => {
    if (data?.productRankingsArray?.length) {
      setCurrentDatasetCount(data.productRankingsArray.length);
    }
  }, [data?.productRankingsArray]);

  async function updateRankScore(data) {
    try {
      const response = await api.patch(
        `${webSocketBaseURL}/transaction-request-update`, data
      );
      if (response.status !== 200) {
        toast.error("Failed to update discovery");
      }

    } catch (error) {
      toast.error("Failed to update discovery");
      throw error;
    }
  }

  const handleSentimentDrivers = async () => {
    const payload = {
      sentimentMeta: data?.sentimentMeta,
    };
    try {
      setIsDriversLoading(true);
      const response = await api.post("/metrics/sentiment-drivers", payload);
      setSentimentDrivers(response.data);
    } catch (err) {
    } finally {
      setIsDriversLoading(false);
    }
  };

  const stateBrandScoreRef = useRef();

  useEffect(() => {
    if (!isOpen) {
      handleRankReset();
    }
  }, [isOpen]);

  function handleRankChange(item) {
    const { index, name, pIndex } = item;
    let updatedBrandScores = [...brandAuditContext];
    const brandItemIndex = updatedBrandScores.findIndex(
      (item) => item.name.toLowerCase().trim() === name.toLowerCase().trim()
    );
    let rankMatchesIndices =
      updatedBrandScores[brandItemIndex]?.extras?.rankMatchesIndices;

    if (brandItemIndex > -1) {
      let indiceItem = rankMatchesIndices.find(
        (item) =>
          item.name.toString().toLowerCase().trim() ===
          name.toString().toLowerCase().trim()
      );
      if (!indiceItem) {
        indiceItem = {
          name,
          indices: Array(currentDatasetCount).fill(-1),
        };
        rankMatchesIndices.push(indiceItem);
      }
      if (savedMatchIndices.length === 0) {
        setSavedReachScore(updatedBrandScores[brandItemIndex].rank);
        setSaveMatchIndices([...indiceItem.indices]);
      }
      indiceItem.indices[pIndex] = index;
      setUserMatchedIndices(indiceItem.indices);
      console.log("updatedBrandScores", updatedBrandScores)
      discoveryCtx.brand_scores[llm][category][market][persona] = updatedBrandScores.map((score) => {
        return {
          ...score,
          extras: {
            ...score.extras,
            rankMatchesIndices: rankMatchesIndices,
          },
        };
      });
    }
    console.log("updatedBrandScoreDetails", discoveryCtx.brand_scores)
    discoveryCtx.replaceBrandScores(discoveryCtx.brand_scores);
  }

  function handleRankReset() {
    if (saveReachScore === undefined || saveReachScore === null) return;
    const name = data.rank.length > 0 ? data.rank[0]?.name : "";
    const indices = structuredClone(
      data.rankMatchesIndices
    );
    const brandProductIndiceItemIndex = indices.findIndex(
      (item) =>
        item.name.toString().toLowerCase().trim() ===
        name.toString().toLowerCase().trim()
    );
    if (brandProductIndiceItemIndex > -1) {
      indices[brandProductIndiceItemIndex] = {
        ...indices[brandProductIndiceItemIndex],
        indices: savedMatchIndices,
      };
    }
    discoveryCtx.brand_scores[llm][category][market][persona] = brandAuditContext.map((brand_score) => {
      const updatedRankArr = structuredClone(brand_score.extras.rank);
      const rankIndex = updatedRankArr.findIndex(
        (item) => item.name.toString().trim() === name.toString().trim()
      );
      let originalRankScore = saveReachScore;
      if (rankIndex > -1) {
        updatedRankArr[rankIndex].score = saveReachScore;
      } else {
        originalRankScore = brand_score.rank;
      }
      return {
        ...brand_score,
        rank: originalRankScore,
        total: originalRankScore + brand_score.favorability,
        extras: {
          ...brand_score.extras,
          rank: updatedRankArr,
          rankMatchesIndices: indices,
        },
      };
    });
    discoveryCtx.replaceBrandScores(discoveryCtx.brand_scores);
    setUserReachScore();
    setSavedReachScore();
    setSaveMatchIndices([]);
    setUserMatchedIndices([]);
  }

  const isIndicesSame = isBothIndicesSame(
    savedMatchIndices,
    userMatchedIndices
  );

  async function handleRankSave() {
    try {
      setIsLoading(true);
      if (!isIndicesSame) {
        await handleRankScoreRefresh();
      }
      const name = data.rank.length > 0 ? data.rank[0]?.name?.toString().trim() : "";

      const updatedDiscoveryState = {
        ...discoveryCtx.state,
        brand_scores: stateBrandScoreRef.current,
      };

      const rankdata = updatedDiscoveryState.brand_scores[llm][category][market][persona].find(
        (item) => item.name?.toString().trim() === name
      );
      if (rankdata) {
        const rankobj = rankdata.extras?.rank?.[0] || {};
        const rankScore = updatedDiscoveryState.rank_scores[llm][category][market][persona];

        if (Array.isArray(rankScore?.data)) {
          rankScore.data = rankScore.data.map((item) =>
            item.name === rankdata.name ? { ...item, ...rankobj } : item
          );
        }

        if (Array.isArray(rankScore?.extras?.rank)) {
          rankScore.extras.rank = rankScore.extras.rank.map((item) =>
            item.name === rankdata.name ? { ...item, ...rankobj } : item
          );
        }
        if (rankScore) {
          const { request_id } = rankScore;

          const payload = {
            transaction_id: discoveryCtx.state.transactionId,
            request_id,
            data: rankScore.data,
            rank: rankScore.extras?.rank,
            rankMatchesIndices: rankScore.extras?.rankMatchesIndices,
          };

          await updateRankScore(payload);
          setUserReachScore();
          setSavedReachScore();
          setSaveMatchIndices([]);
          setUserMatchedIndices([]);
          remountChild();
        }
      } else {
        console.warn("No matching rankdata found");
      }

    } catch (error) {
      console.log("An error occurred");
    } finally {
      setIsLoading(false);
    }
  }
  function isBothIndicesSame(arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((item, index) => item === arr2[index]);
  }

  async function handleRankScoreRefresh() {
    try {
      setLoadingUpdatedRank(true);
      const name = data.rank.length > 0 ? data.rank[0]?.name : "";
      const rankExecCount = userMatchedIndices?.length ||
        data?.productRankingsArray?.length ||
        currentDatasetCount ||
        5;
      const response = await api.post("/metrics/generate-rank-score", {
        indices: userMatchedIndices,
        name,
        rank_exec_count: rankExecCount
      });
      const rankScore = response.data.rankingObject[name] || 0;
      discoveryCtx.brand_scores[llm][category][market][persona] = brandAuditContext.map(
        (brand_score) => {
          const updatedRankArr = structuredClone(brand_score.extras.rank);
          const rankIndex = updatedRankArr.findIndex(
            (item) => item.name.toString().trim() === name.toString().trim()
          );
          if (rankIndex > -1) {
            updatedRankArr[rankIndex].score = rankScore;
          }
          setUserReachScore(rankScore);
          if (
            name.toString().toLowerCase().trim() ===
            brand_score.name.toString().toLowerCase().trim()
          ) {
            return {
              ...brand_score,
              rank: rankScore,
              total: parseInt(rankScore) + brand_score.favorability,
              extras: {
                ...brand_score.extras,
                rank: updatedRankArr,
              },
            };
          } else {
            return brand_score;
          }
        }
      );
      stateBrandScoreRef.current = discoveryCtx.brand_scores;
      discoveryCtx.replaceBrandScores(discoveryCtx.brand_scores);
    } catch (error) {
      throw error;
    } finally {
      setLoadingUpdatedRank(false);
    }
  }

  const {
    infoModalOpen, infoModalTitle, infoModalContent,
    closeInfoModal, hasSections, productMappingAttribute,
    handleInfoClick, handleAllBaseResponsesClick,
  } = useBaseResponsesModal(data, "visibility");

  const pdfRef = useRef();

  const exportToPDF = useReactToPrint({
    contentRef: pdfRef,
    // documentTitle: `${pdfTitle}`,
    documentTitle: `${brandProduct || 'brand'}_Rank_Data.pdf`,
    removeAfterPrint: true,
    pageStyle: `
          @page {
            size: 380mm 200mm;
            margin-left: 10mm;
            margin-right: 10mm;
            margin-bottom: 5mm;
            margin-top: 15mm;
          }
          @media print {
          .parent-container {
      overflow: hidden;
    }
            body {
              -webkit-print-color-adjust: exact; /* Ensures accurate color printing */
            }
            .page-footer {
              position: fixed;
              bottom: 10mm;
              left: 10mm;
              right: 10mm;
              font-size: 20pt;
              color: #777;
              text-align: center;
            }
          }
        `,
  });

  if (pathname.includes("discovery")) {
    selectedOptionShow = type
      ? `${type.charAt(0).toUpperCase()}${type.slice(1)}`
      : selectedOptionShow;
  }

  return (
    <>
      <h3>
        {`${selectedOptionShow} Index Score`}
      </h3>
      {
        <Typography variant="button" color="textSecondary">
          {data?.categoryName}
        </Typography>
      }

      {/* {isMonitoring && (
        <p style={{ color: "gray", fontStyle: "italic", marginTop: "10px" }}>
          Note: The displayed metadata is from one of the datasets in the
          selected timeframe, while the scores on the UI represent the average
          of the same timeframe.
        </p>
      )}
      <hr /> */}
      <div className="mx-4">
        <TableContainer>
          {showRank && <><div className="d-flex justify-content-between align-items-center">
            <h5>{`1. ${discoveryCtx?.mapMetricTitle?.(rankLabel) ?? rankLabel}`}</h5>
            {!isIndicesSame && (
              <div className={classes.actions}>
                <MUIButton
                  variant="outlined"
                  color="error"
                  disabled={isLoading}
                  onClick={handleRankReset}
                >
                  Discard
                </MUIButton>
                <MUIButton loading={isLoading} onClick={handleRankSave}>
                  Save
                </MUIButton>
              </div>
            )}
          </div>
          <hr />
          {data?.productRankingsArray && (
            <div className="container" id="reach_id">
              <div className="row">
                <div
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <h6 style={{ margin: 0 }}>{`A. ${discoveryCtx?.mapMetricTitle?.(`${rankLabel} Data`) ?? `${rankLabel} Data`}`}</h6>
                    {data?.baseResponses?.length > 0 && (
                      <InfoOutlinedIcon
                        className={classes.info_icon}
                        onClick={handleAllBaseResponsesClick}
                      />
                    )}
                  </div>
                  <DownloadIcon
                    onClick={exportToPDF}
                    style={{ cursor: "pointer" }}
                  />
                </div>

                <hr />
                <div
                  style={{ display: "flex", flexDirection: hasSections ? "column" : "row" }}
                  id="print-pdf"
                  ref={pdfRef}
                >
                  {hasSections ? (
                    (() => {
                      let globalIndex = 0;

                      return Object.entries(productMappingAttribute).map(
                        ([sectionKey, indices]) => {
                          const sectionIndices = indices.map(() => globalIndex++); 
                          return (
                            <div key={sectionKey} className={classes.section_block}>
                              <div className={classes.section_header}>
                                <h6 style={{ margin: 0 }}>
                                  {formatSectionLabel(sectionKey)}
                                </h6>
                                {!isCompanyUser && (
                                  <Tooltip title="View Base Prompt" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleInfoClick(sectionKey, indices)}
                                      sx={{
                                        padding: '4px',
                                        '&:hover': {
                                          backgroundColor: 'primary.light',
                                          color: 'primary.main'
                                        }
                                      }}
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}

                              </div>
                              <div style={{ display: "flex" }}>
                                <Rankings
                                  onRankChange={handleRankChange}
                              rankings={indices.map(
                                (i) => data.productRankingsArray[i]
                              )}
                              pIndices={indices}
                                  rankMatchesIndices={data?.rankMatchesIndices}
                              name={
                                data?.favorabilities?.length > 0
                                  ? data.favorabilities[0]?.name
                                  : ""
                              }
                              sectionIndices={sectionIndices}
                                />
                              </div>
                            </div>
                          );
                        }
                      );
                    })()
                  ) : (
                    <Rankings
                      onRankChange={handleRankChange}
                      rankings={data?.productRankingsArray}
                      rankMatchesIndices={data?.rankMatchesIndices}
                      name={
                        data?.favorabilities?.length > 0
                          ? data.favorabilities[0]?.name
                          : ""
                      }
                    />
                  )}
                </div>
                <hr />
              </div>
            </div>
          )}
          <h6>
            {data?.productRankingsArray ? "B" : "A"}. {discoveryCtx?.mapMetricTitle?.(`Average ${rankLabel} Scores`) ?? `Average ${rankLabel} Scores`}
          </h6>
          <hr />
          <Table sx={{ minWidth: 650 }} size="medium" aria-label="simple table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell align="right">Score</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {data?.rank && data?.rank?.length > 0
                ? data?.rank.length === 1
                  ? data?.rank?.map((row) => (
                    <StyledTableRow
                      key={row.name}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <StyledTableCell component="th" scope="row">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        <span>{row.score}</span>
                        {!isIndicesSame && (
                          <RefreshIcon
                            onClick={handleRankScoreRefresh}
                            className={
                              loadingUpdatedRank
                                ? classes.rotate
                                : classes.update_rank
                            }
                          />
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                  : data?.rank
                    ?.filter((item) => item.name === brandProduct)
                    ?.map((row) => (
                      <StyledTableRow
                        key={row.name}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <StyledTableCell component="th" scope="row">
                          {row.name}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          {row.score}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                : null}
            </TableBody>
          </Table>
          <hr /></>}
          {showSentiment && <><h5>{`2. ${discoveryCtx?.mapMetricTitle?.("Sentiment") ?? "Sentiment"}`}</h5>
          <hr />
          <SentimentTable
            data={data}
            selectedInput={selectedInput}
            fromDashboardBrandScore={fromBrandAudit}
            compIndexScoreMetaData={compIndexScoreMetaData}
            brandProduct={brandProduct}
            showInfoIcon={!isCompanyUser}
          />
          <hr /></>}
          {showDrivers && <><div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <h5>{`3. ${discoveryCtx?.mapMetricTitle?.("Sentiment Drivers") ?? "Sentiment Drivers"}`}</h5>
            {!sentimentDrivers ? (
              !isDriversLodaing ? (
                <MUIButton
                  onClick={handleSentimentDrivers}
                  variant="outlined"
                  // className={classes.regenerate}
                  startIcon={<AutoAwesomeOutlinedIcon />}
                >
                  Generate
                </MUIButton>
              ) : (
                <Spinner />
              )
            ) : (
              isFactorGroupedDrivers && driverFactorOptions.length > 0 && (
                <div style={{ minWidth: 220 }}>
                  <MUISelect
                    fullWidth={false}
                    optionValue={selectedDriverFactor}
                    options={driverFactorOptions.map((f) => ({ name: f, value: f }))}
                    onChange={(e) => setSelectedDriverFactor(e.target.value)}
                  />
                </div>
              )
            )}
          </div>
          {sentimentDrivers && Object.keys(sentimentDrivers).length > 0 && (
            <>
              {/* <hr />
                <h5>3. Sentiment Drivers</h5> */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
                id="sentimentdrivers_id"
              >
                {positiveDriversToShow.length > 0 && (
                    <div style={{ flex: 1, minWidth: "40%" }} >
                      <SentimentDriversTable
                        data={transformDriverData(
                          positiveDriversToShow,
                          "Positive"
                        )}
                        title={discoveryCtx?.mapMetricTitle?.("Positive Sentiment Drivers") ?? "Positive Sentiment Drivers"}
                        id="positive_sentimentdrivers_id"
                        driverType="positive"
                        sentimentData={data}
                        showInfoIcon={!isCompanyUser}
                      />
                    </div>
                  )}

                {negativeDriversToShow.length > 0 && (
                    <div style={{ flex: 1, minWidth: "40%" }}>
                      <SentimentDriversTable
                        data={transformDriverData(
                          negativeDriversToShow,
                          "Negative"
                        )}
                        title={discoveryCtx?.mapMetricTitle?.("Negative Sentiment Drivers") ?? "Negative Sentiment Drivers"}
                        id="negative_sentimentdrivers_id"
                        driverType="negative"
                        sentimentData={data}
                        showInfoIcon={!isCompanyUser}
                      />
                    </div>
                  )}
              </div>
            </>
          )}
          </>}

          {/* Sections 4 / 5 / 6 — Brand Pillars / Product Dimensions, Purchase Criteria, RBI Formula */}
          {(showPillars || showPurchase || showFormula) && (() => {
            const isProduct = discoveryCtx.type === "product";
            const pillarsLabel = isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;
            const coreAttributesData = discoveryCtx.core_attributes?.[llm]?.[category]?.[market]?.[persona];
            const categoryDimensionsData = discoveryCtx.category_dimensions?.[llm]?.[category]?.[market]?.[persona];
            const taxonomiesData = discoveryCtx.taxonomies?.[llm]?.[category]?.[market]?.[persona];
            const brandScoreEntry = compIndexScoreMetaData.find(
              (bs) => (bs.name || "").toLowerCase().trim() === (brandProduct || "").toLowerCase().trim()
            ) || compIndexScoreMetaData[0];
            const brandPillarsAvg = isProduct
              ? getProductDimensionsAvg(brandProduct, taxonomiesData)
              : getBrandPillarsAvg(brandProduct, coreAttributesData);
            const purchaseCriteriaAvg = getPurchaseCriteriaAvg(brandProduct, categoryDimensionsData);
            return (
              <>
                {showPillars && <>
                  <hr />
                  <h5>{`4. ${pillarsLabel}`}</h5>
                  <hr />
                  {isProduct
                    ? <ProductDimensionsTable brandProduct={brandProduct} taxonomiesData={taxonomiesData} />
                    : <BrandPillarsTable brandProduct={brandProduct} coreAttributesData={coreAttributesData} />
                  }
                </>}
                {showPurchase && <>
                  <hr />
                  <h5>5. Purchase Criteria</h5>
                  <hr />
                  <PurchaseCriteriaTable brandProduct={brandProduct} categoryDimensionsData={categoryDimensionsData} />
                </>}
                {showFormula && <>
                  <hr />
                  <h5>6. Brand Index Score Formula</h5>
                  <hr />
                  <RBIFormulaBreakdown
                    rank={brandScoreEntry?.rank}
                    favorability={brandScoreEntry?.favorability}
                    brandPillarsAvg={brandPillarsAvg}
                    purchaseCriteriaAvg={purchaseCriteriaAvg}
                    pillarsLabel={pillarsLabel}
                  />
                </>}
              </>
            );
          })()}
        </TableContainer>
      </div>
      <BaseResponsesModal
        open={infoModalOpen}
        title={infoModalTitle}
        content={infoModalContent}
        onClose={closeInfoModal}
      />
    </>
  );
}
